import React, { Fragment, PureComponent } from 'react';

import styled, { css } from 'styled-components';

import { compose } from 'redux';

import AppIcon from '../atoms/AppIcon/AppIcon';
import Button from '../atoms/Button/Button';
import Text from '../atoms/Text/Text';
import VideoPopup from '@components/organisms/VideoPopup';

import { buildLocalizedVideoUrl } from '@utils/calculateVideoPreviewUrl';
import { isCN, isStaging } from '@utils/origin';

import { Color } from '@shared/enums/button-color';
import ElsevierLogoIcon from '@public/icons/elsevierPrimaryLogoGraphiteRGB.svg';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { ORIGIN, withTranslation } from 'i18n';
import PlayAbyassIcon from '@public/icons/playAbyss.svg';
import { WithRouterProps } from 'next/dist/client/with-router';
import { breakpoint } from 'theme';
import { env } from 'next-runtime-env';
import { withRouter } from 'next/router';

const Wrapper = styled.div`
  ${breakpoint.lg`
    width: 56.25em;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const Customer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 16.25em;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2em;
  height: 5em;
  align-self: center;

  ${breakpoint.m`
    max-width: 25.625em;
    margin-bottom: 4em;
    height: 7.375em;
  `}
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Plus = styled.div`
  background: ${props => props.theme.colors.white};
  height: 1.25em;
  width: 0.25em;
  position: relative;

  &:after {
    content: '';
    height: 0.25em;
    width: 1.25em;
    background: #ffffff;
    position: absolute;
    left: -9px;
    top: 0.5em;
  }
`;

const AppIconExtend = styled(AppIcon)`
  margin-bottom: 0.25em;

  ${breakpoint.m`
    margin-bottom: 0.75em;
  `}
`;

const ElsevierLogoIconExtend = styled.img`
  height: 5em;
  width: 10.2em;

  ${breakpoint.m`
    height: 12.5em;
    width: 25.5em;
  `}
`;

const PromoImage = styled.img`
  height: 6.25em;

  ${breakpoint.m`
    height: 12.5em;
  `}
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 2em;
  line-height: 1.16;
  color: ${props => props.theme.colors.kuroiBlack};
  text-align: center;
  margin-bottom: 2rem;
  max-width: 39.375rem;
  margin-left: auto;
  margin-right: auto;

  ${breakpoint.m`
    margin-bottom: 4rem;
    font-size: 3.25em;
  `}
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 2em;

  ${breakpoint.m`
    flex-direction: row;
    margin-bottom: 4em;
  `}

  ${breakpoint.lg`
    margin-bottom: 5.25em;
  `}
`;

const ButtonWatchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const PreviewVideoWrapper = styled.div`
  padding-bottom: 3em;
`;

const sharedButtonCss = css`
  width: 18rem;
  height: 4.125rem;
  font-size: 1.25em;
  text-transform: uppercase;
`;

const ButtonBuy = styled(Button)<{ isPromo: boolean }>`
  ${sharedButtonCss};
  margin-bottom: 1em;

  ${breakpoint.m`
    margin-bottom: 0;
    margin-right: ${props => (props.isPromo ? 0 : 2)}em;
  `}
`;

const ButtonWatch = styled(Button)`
  ${sharedButtonCss};
  border-width: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewVideo = styled.video`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: block;

  ${breakpoint.m`
    width: 43.875em;
  `}

  ${breakpoint.lg`
    width: 56.25em;
  `}
`;

type PresentationProps = WithRouterProps & {
  appName: string;
  appIcon: string;
  onClickScroll: () => void;
  className: string;
  t: I18nTranslation;
  discountPercent?: string;
  isPromo?: boolean;
  imageUrl?: string;
};

class Presentation extends PureComponent<PresentationProps> {
  state = {
    videoUrl: null,
  };
  src = '';

  t = key => this.props.t(`presentation.${key}`);

  handleClickWatch = () => {
    if (ORIGIN === 'completeanatomy.cn') {
      this.setState({
        videoUrl:
          'https://cdn.completeanatomy.cn/common/ca-inapp/1927073659.mp4',
      });
    } else {
      this.setState({
        videoUrl:
          'https://cdn.3d4medical.com/media/homepage/DiscoverCompleteAnatomy2022.mp4',
      });
    }
  };

  handleCloseVideo = () => {
    this.setState({
      videoUrl: null,
    });
  };

  renderButtonWatch = () => {
    return (
      <ButtonWatch
        colorType={Color.FullTransparentBorderAbyss}
        onClick={this.handleClickWatch}
      >
        <PlayAbyassIcon width={24} height={24} style={{ marginRight: '0.5em' }} />
        {this.t('video')}
      </ButtonWatch>
    );
  };

  componentDidMount() {
    this.src = buildLocalizedVideoUrl();
  }

  componentDidUpdate() {
    this.src = buildLocalizedVideoUrl();
  }

  render() {
    const {
      className,
      t: tProp,
      appIcon,
      appName,
      onClickScroll,
      discountPercent,
      isPromo,
      imageUrl,
    } = this.props;
    const buttonBuyText = isPromo ? 'claimOffer' : 'buy';
    const isProd = env('NEXT_PUBLIC_ENVIRONMENT') === 'production';

    function getCdnBase(type: 'upload' | 'logo', isCN: boolean, isProd: boolean) {
      if (isStaging) {
        if (type === 'upload') {
          return 'https://cdn.3d4medical.com/accounts-staging/upload/';
        } else {
          return 'https://cdn.3d4medical.com/common/store/images/';
        }
      }
      if (isCN) {
        if (type === 'upload') {
          return isProd
            ? 'https://cdn.completeanatomy.cn/accounts/upload/'
            : 'https://cdn.nonprod.completeanatomy.cn/accounts/upload/';
        } else {
          return isProd
            ? 'https://cdn.completeanatomy.cn/common/store/images/'
            : 'https://cdn.nonprod.completeanatomy.cn/common/store/images/';
        }
      } else {
        if (type === 'upload') {
          return isProd
            ? 'https://cdn.3d4medical.com/accounts/upload/'
            : 'https://cdn.nonprod.3d4medical.com/accounts/upload/';
        } else {
          return isProd
            ? 'https://cdn.3d4medical.com/common/store/images/'
            : 'https://cdn.nonprod.3d4medical.com/common/store/images/';
        }
      }
    }

    function getImageUrl(imageUrl: string): string | null {
      if (!imageUrl) return null;
      
      return `${getCdnBase('upload', isCN, isProd)}${imageUrl}`;
    }

    const promoImage = getImageUrl(imageUrl);
    const fallbackImage = `${getCdnBase('logo', isCN, isProd)}elsevier_logo.svg`;
      
    return (
      <div className={className}>
        <Wrapper>
          <Customer>
            {promoImage 
              ? <PromoImage src={promoImage} alt="Promo Image" />
              : <ElsevierLogoIconExtend src={fallbackImage} alt="Elsevier logo" />
            }
          </Customer>
          <Title>
            {isPromo
              ? tProp(`presentation.discountTitle`, { discountPercent })
              : this.t('title')}
          </Title>
          <Buttons>
            <ButtonBuy
              isPromo={isPromo}
              colorType={Color.Abyss}
              onClick={onClickScroll}
            >
              {this.t(buttonBuyText)}
            </ButtonBuy>
            {!isPromo && this.renderButtonWatch()}
          </Buttons>
          <PreviewVideoWrapper>
            <PreviewVideo
              poster="https://cdn.3d4medical.com/media/complete-anatomy-3/2019/screens.jpg"
              src={this.src}
              muted
              preload="auto"
              loop
              autoPlay
              playsInline
            />
          </PreviewVideoWrapper>
          {isPromo && (
            <Fragment>
              <Title>{this.t('title')}</Title>
              <ButtonWatchWrapper>
                {this.renderButtonWatch()}
              </ButtonWatchWrapper>
            </Fragment>
          )}
        </Wrapper>

        {this.state.videoUrl && (
          <VideoPopup
            onClose={this.handleCloseVideo}
            url={this.state.videoUrl}
          />
        )}
      </div>
    );
  }
}

const StyledPresentation = styled(Presentation)`
  padding: 3.375em 1em 4em;
  background: linear-gradient(
    to bottom,
    ${props => props.theme.colors.white} 0%,
    ${props => props.theme.colors.white} 60%,
    ${props => props.theme.colors.cornflowerBlue} 60%,
    ${props => props.theme.colors.cornflowerBlue} 100%
  );

  ${breakpoint.m`
    padding-top: 4.875em;
    padding-bottom: 6em;
    padding-left: 2em;
    padding-right: 2em;
  `}

  ${breakpoint.lg`
    padding-top: 6.25em;
    padding-left: 0;
    padding-right: 0;
  `}
`;

export default compose<PresentationProps>(
  withRouter,
  withTranslation('offer')
)(StyledPresentation);
